import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿// pages/ConfigHorasRegaloPage.tsx
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import { fetchHorasRegaloConfigs, HorasRegaloConfig } from '../../../services/horasRegaloConfig';
import AddConfigrHoras from '../../../components/config-horas-regalo/AddConfigHoras';
import EditConfigHoras from '../../../components/config-horas-regalo/EditConfigHoras';
import DeleteConfigHoras from '../../../components/config-horas-regalo/DeleteConfigHoras';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const ConfigHorasRegaloPage: React.FC = () => {
    const [configs, setConfigs] = useState<HorasRegaloConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<HorasRegaloConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('horasregaloconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (perm?.can_read) {
                const data = await fetchHorasRegaloConfigs();
                setConfigs(data);
            } else {
                setConfigs([]);
            }
        } catch (err) {
            setError('Error al cargar las configuraciones');
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadConfigs();
        }
    }, [loadConfigs, perm]);

    const handleRefresh = () => {
        loadConfigs();
    };

    const handleConfigCreated = (newConfig: HorasRegaloConfig) => {
        setConfigs(prev => [...prev, newConfig]);
    };

    const handleConfigUpdated = (updatedConfig: HorasRegaloConfig) => {
        setConfigs(prev => prev.map(config => 
            config.id === updatedConfig.id ? updatedConfig : config
        ));
    };

    const handleConfigDeleted = (deletedConfigId: number) => {
        setConfigs(prev => prev.filter(config => config.id !== deletedConfigId));
    };

    const openEditModal = (config: HorasRegaloConfig) => {
        setSelectedConfig(config);
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (config: HorasRegaloConfig) => {
        setSelectedConfig(config);
        setIsDeleteModalOpen(true);
    };

    const getPlanServicioResumido = (config: HorasRegaloConfig) => {
        if (config.precio_plan_servicio_info) {
            return config.precio_plan_servicio_info;
        }
        return `${config.servicio_asociado_nombre} - ${config.plan_config_info}`;
    };

    const getPreciosPermitidosResumido = (config: HorasRegaloConfig) => {
        if (config.precios_plan_permitidos_info && config.precios_plan_permitidos_info.length > 0) {
            return config.precios_plan_permitidos_info
                .map(pps => pps.plan_servicio)
                .slice(0, 2)
                .join(', ') + (config.precios_plan_permitidos_info.length > 2 ? '...' : '');
        }
        return 'Todos los precios';
    };

    const sortedConfigs = [...configs].sort((a, b) => a.horas_asignadas - b.horas_asignadas);
    const totalPages = Math.ceil(sortedConfigs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = sortedConfigs.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando horas de regalo..." />;

    if (error) {
        return (
            <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    return <><LumaResourceWorkspace title="Configuración de horas de regalo" description="Consulta y gestiona la información de tu coworking." group="Configuraciones" rows={configs} columns={[{key:'id',label:'Configuración'},{key:'servicio_asociado_nombre',label:'Servicio'},{key:'horas_asignadas',label:'Horas asignadas'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedConfig(r);setIsEditModalOpen(true);}:undefined} onDelete={perm?.can_delete?r=>{setSelectedConfig(r);setIsDeleteModalOpen(true);}:undefined} history=""/><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{isModalOpen && (<AddConfigrHoras
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onCreated={handleConfigCreated}
                    />)}{isEditModalOpen && selectedConfig && (<EditConfigHoras
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onUpdated={handleConfigUpdated}
                        config={selectedConfig}
                    />)}{isDeleteModalOpen && selectedConfig && (<DeleteConfigHoras
                        isOpen={isDeleteModalOpen}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onDeleted={handleConfigDeleted}
                        config={selectedConfig}
                    />)}</>;
};

export default ConfigHorasRegaloPage;
